/**
 * components/riskTerminal/api.js
 *
 * The Risk Terminal's write actions (upstox-037), each a call to an endpoint
 * that already exists under /upstox (routers/upstox_structures.py). Nothing
 * here places, modifies or cancels an order.
 *
 * Every call resolves to { ok: true, data } or { ok: false, status, message,
 * detail } — never throws — so the screen can always show the server's own
 * refusal where the action was taken, and never change anything the server
 * has not accepted.
 */

import { withAccount } from './accounts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** The server's refusal in words: {detail:{message}} (409), {detail:"…"} (404/422/403), or a validation list. */
export function refusalMessage(status, body) {
  const d = body && body.detail;
  if (d && typeof d === 'object' && !Array.isArray(d) && d.message) return d.message;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) return d.map(x => x.msg || String(x)).join('; ');
  if (status === 401) return 'Signed out — sign in again.';
  if (status === 403) return 'Your account is not allowed to make changes.';
  return 'The server refused this (HTTP ' + status + ').';
}

async function call(method, path, body) {
  try {
    const res = await fetch(`${API}/upstox${withAccount(path)}`, {
      method, credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (res.ok) return { ok: true, data };
    return { ok: false, status: res.status, message: refusalMessage(res.status, data), detail: data && data.detail };
  } catch (e) {
    return { ok: false, status: 0, message: 'Could not reach the server — nothing was changed.', detail: null };
  }
}

export const realApi = {
  createStructure: (name, book) => call('POST', '/structures', { name, book }),
  rename: (id, name) => call('PATCH', `/structures/${id}`, { name }),
  close: (id, force) => call('POST', `/structures/${id}/close`, { force: !!force }),
  assign: (legId, target) => call('POST', `/legs/${legId}/assign`, target),          // {structure_id} | {new_structure:{name,book}}
  move: (legId, structureId) => call('POST', `/legs/${legId}/move`, { structure_id: structureId }),
  unassign: legId => call('POST', `/legs/${legId}/unassign`, {}),
  holding: (legId, target) => call('POST', `/legs/${legId}/reclassify-holding`, target), // {structure_id} | {new_structure_name}
  declareIntent: (orderId, intent) => call('POST', `/orders/${encodeURIComponent(orderId)}/intent`, { intent }),
  awaitingIntent: earlier => call('GET', '/orders/awaiting-intent' + (earlier ? '?earlier=true' : '')),
  answer: (episodeId, response, holdReason) => call('POST', `/episodes/${episodeId}/response`,
    response === 'HOLD' ? { response, hold_reason: holdReason } : { response }),
  dismiss: conditionId => call('POST', `/conditions/${conditionId}/dismiss`, {}),
  // upstox-044: the trend reading this user is judged against
  switchReading: (reading, confirm) => call('POST', '/trend-reading', { reading, confirm: !!confirm }),
  // upstox-043: the watchlist's fixed list
  watchSearch: q => call('GET', '/watchlist/search?q=' + encodeURIComponent(q)),
  watchAdd: (listId, instrumentKey) => call('POST', `/watchlist/${listId}/items`, { instrument_key: instrumentKey }),
  watchRemove: (listId, itemId) => call('POST', `/watchlist/${listId}/items/${itemId}/remove`, {}),
};
